// shared/choices.ts: Big Choices, the big questions about the house a family answers on their board (docs/DECISIONS.md
// "Big Choices"). The questions and options are homebuild's own, never the builder's, and stop well short of a Scope
// of Work. Every question has "Not sure yet", which counts as answered: someone who hasn't decided has told the team
// where to start the conversation. Pure data and helpers, used by the Worker (validation, the brief) and the SPA.
import type { ProjectKind } from "./api";

/** The stored value for "Not sure yet". */
export const UNSURE = "unsure";
export const UNSURE_LABEL = "Not sure yet";

export type ChoiceOption = { id: string; label: string; art: string };
export type ChoiceQuestion = {
  id: string;
  /** Short label for lists, the brief and activity ("Basement"). */
  label: string;
  /** The question as the family reads it. */
  ask: string;
  /** The tenant room it belongs to, for the eyebrow ("Kitchen & Dining"); the label stands in when the board has no such room. */
  room: string;
  options: readonly ChoiceOption[];
  /** For the team: one open question that starts the conversation when the family is not sure yet. */
  opener: string;
};

const art = (name: string) => `/selections/${name}.webp`;

/** House order: the shape of the house first, then the rooms you stand in, then what's under the floor. */
export const CHOICE_QUESTIONS: readonly ChoiceQuestion[] = [
  {
    id: "stories", label: "Stories", ask: "How many floors?", room: "exterior",
    options: [
      { id: "one", label: "One story", art: art("stories-one") },
      { id: "one-half", label: "One and a half", art: art("stories-one-half") },
      { id: "two", label: "Two story", art: art("stories-two") },
    ],
    opener: "What does an ordinary day look like: everything on one floor, or bedrooms upstairs?",
  },
  {
    id: "basement", label: "Basement", ask: "What kind of basement?", room: "lower-level",
    options: [
      { id: "none", label: "No basement", art: art("basement-none") },
      { id: "partial", label: "Partial", art: art("basement-partial") },
      { id: "full", label: "Full", art: art("basement-full") },
      { id: "walkout", label: "Walk-out", art: art("basement-walkout") },
    ],
    opener: "If you had a lower level, how would you use it?",
  },
  {
    id: "siding", label: "Siding", ask: "What kind of siding?", room: "exterior",
    options: [
      { id: "vinyl", label: "Vinyl", art: art("siding-vinyl") },
      { id: "fiber-cement", label: "Fiber cement", art: art("siding-fiber-cement") },
      { id: "board-batten", label: "Board and batten", art: art("siding-board-batten") },
      { id: "cedar-shake", label: "Cedar shake", art: art("siding-cedar-shake") },
    ],
    opener: "Which houses have caught your eye, and what did you like about the outside?",
  },
  {
    id: "garage", label: "Garage", ask: "How many garage bays?", room: "garage",
    options: [
      { id: "2", label: "2 bays", art: art("garage-2") },
      { id: "3", label: "3 bays", art: art("garage-3") },
      { id: "4", label: "4 bays", art: art("garage-4") },
    ],
    opener: "What will live in the garage besides the cars?",
  },
  {
    id: "island", label: "Island", ask: "An island in the kitchen?", room: "kitchen",
    options: [
      { id: "none", label: "No island", art: art("island-none") },
      { id: "one", label: "One island", art: art("island-single") },
      { id: "two", label: "Two islands", art: art("island-double") },
    ],
    opener: "How do you use the kitchen on a busy night?",
  },
  {
    id: "primary", label: "Primary suite", ask: "Main floor or upstairs?", room: "primary-suite",
    options: [
      { id: "main", label: "Main floor", art: art("primary-first") },
      { id: "up", label: "Upstairs", art: art("primary-second") },
    ],
    opener: "Stairs every night, or everything you need on the main floor?",
  },
  {
    id: "water", label: "Water", ask: "City water or a well?", room: "land",
    options: [
      { id: "city", label: "City water", art: art("water-city") },
      { id: "well", label: "A well", art: art("water-well") },
    ],
    opener: "Has anyone looked at water for the lot yet?",
  },
  {
    id: "sewer", label: "Sewer", ask: "Town sewer or septic?", room: "land",
    options: [
      { id: "sewer", label: "Town sewer", art: art("sewer-sewer") },
      { id: "septic", label: "Septic", art: art("sewer-septic") },
    ],
    opener: "Has the lot been checked for sewer or septic?",
  },
  {
    id: "heat", label: "Heat", ask: "How will you heat it?", room: "whole-house",
    options: [
      { id: "gas", label: "Natural gas", art: art("heat-gas") },
      { id: "propane", label: "Propane", art: art("heat-propane") },
      { id: "pump", label: "Heat pump", art: art("heat-pump") },
    ],
    opener: "How do you weigh the monthly bill against the cost up front?",
  },
];

export const CHOICE_COUNT = CHOICE_QUESTIONS.length;

export function choiceQuestion(id: string): ChoiceQuestion | undefined {
  return CHOICE_QUESTIONS.find((q) => q.id === id);
}

/** Whether `value` answers question `qid`: one of its options, or "Not sure yet". */
export function isChoiceValue(qid: string, value: string): boolean {
  const q = choiceQuestion(qid);
  return !!q && (value === UNSURE || q.options.some((o) => o.id === value));
}

/** "Walk-out", "Not sure yet", or null for an unknown value. */
export function choiceLabel(qid: string, value: string): string | null {
  if (value === UNSURE) return choiceQuestion(qid) ? UNSURE_LABEL : null;
  return choiceQuestion(qid)?.options.find((o) => o.id === value)?.label ?? null;
}

/** The illustration of an answer: none for "Not sure yet" (someone who hasn't decided hasn't picked a picture). */
export function choiceArt(qid: string, value: string, size: "full" | "small" = "full"): string | null {
  const src = choiceQuestion(qid)?.options.find((o) => o.id === value)?.art ?? null;
  return src && size === "small" ? smallArt(src) : src;
}

/** The 480 px variant of an illustration. */
export function smallArt(src: string): string {
  return src.replace(/\.webp$/, ".480.webp");
}

/** Big Choices are about building a house: a remodel board doesn't ask them. */
export function choicesApply(kind: ProjectKind): boolean {
  return kind !== "remodel";
}
